import React, {useCallback, useEffect} from "react";
import {useLocation, useParams} from "react-router-dom";
import moment from "moment/moment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {APIPostWorkPlanStudents} from "../api/workPlan";
import {useAuth} from "hooks/useAuth";
import GenericButton from "shared/ui/GenericButton";
import PageTitle from "shared/ui/PageTitle";
import BoxContent from "shared/ui/BoxContent";
import CustomButton from "shared/ui/CustomButton";
import Icon from "shared/ui/Icon";
import StyledLink from "shared/ui/LinkStyled";
import CustomDataTable from "shared/tables/CustomDataTable";
import {APICreateTestTraining} from "../../tests/create/api/createTestsTraining";
import {getTableColumns} from "../components/_tableColumns";
import useDataTable from "../../../hooks/useDataTable";

interface IStudent {
    student_id: number;
    student_name: string;
    subcategory_score: number;
    next_level: number;
    recommended_tests_no: number;
    realized_tests_no: number;
    remaining_tests_no: number;
}

type LocationProps = {
    state: {
        classroomId: string;
        classroomName: string;
        categoryName: string;
        categoryId: string;
        subjectName: string;
        grade: string;
        gradeId: string;
    };
};

const WorkPlan = () => {
    const {id = ""} = useParams();
    const {update, user} = useAuth();
    const isTeacher = user.position === "teacher";

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        tableColumns,
        setTableColumns,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        selectedRows,
        setSelectedHandler,
        page,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "student_name", undefined, "");

    let location = useLocation() as unknown as LocationProps;
    const classroomId = location.state?.classroomId || "";
    const classroomName = location.state?.classroomName || "";
    const categoryName = location.state?.categoryName || "";
    const categoryId = location.state?.categoryId || "";
    const subjectName = location.state?.subjectName || "";
    const grade = location.state?.grade || "";
    const gradeId = location.state?.gradeId || "";

    const mapWorkPlan = useCallback((student: IStudent) => {

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box>
                    <GenericButton
                        disabled={(student.recommended_tests_no - student.realized_tests_no) <= 0}
                        type="test-generate"
                        onClick={(e: any) => {
                            e.stopPropagation();
                            confirmGenerateIndividualTestHandler(student)
                        }}
                    />
                </Box>
            </Box>
        );

        return {
            id: student.student_id,
            student_name: <StyledLink to={`/elevi/${student.student_id}`}>{student.student_name}</StyledLink>,
            subcategory_score: student.subcategory_score,
            next_level: student.next_level ? student.next_level : "-",
            recommended_tests_no: student.recommended_tests_no,
            realized_tests_no: student.realized_tests_no,
            remaining_tests_no: student.recommended_tests_no - student.realized_tests_no,
            ...(isTeacher && {
                disableSelect: (student.recommended_tests_no - student.realized_tests_no) <= 0,
                actions: actions,
            }),
        }
    }, []);

    useEffect(() => {
        if (id !== "") {
            setLoading(true);
            APIPostWorkPlanStudents({
                page: `?page=${page + 1}`,
                work_plan_id: id,
                classroom_id: classroomId,
                teacher_id: user.id,
                sort: orderBy,
                order: order,
                search: submittedQuery,
            })
                .then((response) => {
                    if (response.data.success) {
                        // console.log("APIGetReports OK", response);
                        setTableData(response.data.data.data.map((test: any) => mapWorkPlan(test)));
                        setTotalRows(response.data.data.data.length);
                        setPerPage(response.data.data.data.length);
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch((err) => {
                    // console.log("getClassroom ERR", err);
                    update("error", {show: true, code: "A001"});
                })
                .finally(() => setLoading(false));
        }
    }, [page, orderBy, order, submittedQuery]);

    useEffect(() => {
        const columns = getTableColumns(user.position, "workPlan");
        setTableColumns(columns);
    }, [user.position]);

    const generateTestHandler = (students: any, callback: Function) => {
        let selectedStudents: number[];
        let selectedCategories: number[] = [parseInt(categoryId)];

        if (isNaN(students[0])) {
            selectedStudents = students.map(({student_id}: any) => student_id);
        } else {
            selectedStudents = students;
        }

        update("loading", {show: true, modal: true});
        APICreateTestTraining({
            grade: gradeId,
            classroom_id: classroomId,
            subcategories: selectedCategories,
            users: selectedStudents,
            test_config: 1,
            start_at: moment(new Date(Date.now())).locale("ro").format("YYYY-MM-DD HH:mm:ss"),
            expire: 72,
            items_number: 10,
            difficulty: 2,
            individual_test: 1,
            items: [],
            test_mode: "training",
            item_type_id: 23,
            tag: "PLAN-DE-LUCRU",
            work_plan: id,
            duration: 30,
        })
            .then((response) => {
                if (response.data.success) {
                    update("error", {
                        show: true,
                        button: "Închide",
                        icon: "fat face-smile",
                        closable: true,
                        title: "Generare test",
                        message: (
                            "Sesiunile de testare le-au fost transmise pe e-mail elevilor selectați. " +
                            "Le veți regăsi pe acestea în pagina Teste antrenament, secțiunea Teste active."
                        ),
                    });
                    setSelectedHandler([]);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                if (err?.response?.status === 400) {
                    const errors = err.response.data.data.message;
                    update("error", {
                        show: true,
                        button: "Închide",
                        icon: "fat face-eyes-xmarks",
                        closable: true,
                        title: "Eroare generare test",
                        message: (
                            <>{errors}</>
                        ),
                    });
                    setSelectedHandler([]);
                } else {
                    update("error", {show: true, code: "A001"});
                }
            })
            .finally(() => update("loading", {show: false}));
    }

    const confirmGenerateTestHandler = () => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat face-smile",
            title: "Generare test",
            message: (
                <Typography variant="body1">
                    Un test de antrenament <strong>{subjectName} {grade} | {categoryName}</strong> va fi generat pentru elevii selectați.
                </Typography>
            ),
            returnFunction: (callback: Function) => {
                generateTestHandler(selectedRows, callback);
            },
            buttonYes: "Generează test",
            buttonNo: "Închide",
        });
    }

    const confirmGenerateIndividualTestHandler = (student: IStudent) => {
        update("confirm", {
            show: true,
            closable: true,
            title: "Generare test",
            message: (
                <Typography variant="body1">
                    Un test de antrenament <strong>{subjectName} {grade} | {categoryName}</strong> va fi generat pentru elevul
                    <strong> {student.student_name}.</strong>
                </Typography>
            ),
            returnFunction: (callback: Function) => {
                generateTestHandler([student], callback);
            },
            buttonYes: "Generează test",
            buttonNo: "Închide",
        });
    };

    const selectOptions = (
        <CustomButton
            disabled={selectedRows.length === 0}
            buttonType="textIcon"
            bgcolor="paper"
            sxExtra={{
                marginLeft: 1.5,
                color: "secondary.main",
                backgroundColor: "transparent",
                "&:hover": {
                    color: "secondary.light",
                    backgroundColor: "transparent",
                },
                "&[aria-expanded='true']": {
                    backgroundColor: "white",
                },
                "&:disabled": {
                    color: "text.disabled",
                    backgroundColor: "transparent",
                },
            }}
            onClick={confirmGenerateTestHandler}
            icon={<Icon icon="fas plus" fixedWidth={true}/>}
        >Generează teste</CustomButton>
    );

    return (
        <Box component="main">
            <PageTitle back={true}>Plan de lucru {classroomName}</PageTitle>
            <Typography variant="h3" mb={3}>{subjectName} {grade} | {categoryName} </Typography>
            <BoxContent>
                <CustomDataTable
                    loading={loading}
                    columns={tableColumns}
                    rows={tableData}
                    rowsPerPage={perPage}
                    page={page}
                    totalRows={totalRows}
                    onPageChange={handleChangePage}
                    handleRequestSort={handleRequestSort}
                    orderBy={orderBy}
                    order={order}
                    hasSearch={true}
                    onSearch={handleSearch}
                    onChange={handleSearchChange}
                    searchValue={query}
                    onClearSearch={handleClearSearch}
                    hasSelect={isTeacher}
                    selected={selectedRows}
                    setSelectedHandler={setSelectedHandler}
                    selectOptions={selectOptions}
                />
            </BoxContent>
        </Box>
    )
};

export default WorkPlan;